import type { ColoredMaterial } from "@color-finder/color-finder-api/models";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import type { NamedColor } from "components/filter/color-filter/ColorFilter";
import { useAuthUser } from "hooks/authentication";
import type { FilterEntry } from "hooks/filters";
import type { SortEntry } from "hooks/sort";
import {
  SettingsContext,
  type SettingsContextValue,
} from "providers/SettingsProvider";
import { type Dispatch, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { checkInternalUser } from "service/utils/get-user-type";
import DeltaELabel from "../DeltaELabel";
import { NoSearchResultsTableRow } from "./NoSearchResultsTableRow";
import { SearchResultsLoadingTableRow } from "./SearchResultsLoadingTableRow";
import { SearchResultsTableRow } from "./SearchResultsTableRow";

export interface SearchResultsProps {
  values?: Array<ColoredMaterial>;
  target?: NamedColor;
  onFilterReset?: () => void;
  loading?: boolean;
  sort?: Array<SortEntry>;
  onSortChange?: Dispatch<Array<SortEntry>>;
  isLocalSearch?: boolean;
  /** Active sort of the complete local result list; the table only shows the current page of it. */
  localSort?: SortEntry;
  /** Sorts the complete local result list; `undefined` restores the backend order. */
  onLocalSort?: (sort?: SortEntry) => void;
  onMaterialSelect?: (material: ColoredMaterial) => void;
  filters?: Array<FilterEntry>;
}

export const SearchResultsTable = ({
  values,
  target,
  onFilterReset,
  loading,
  sort,
  onSortChange,
  isLocalSearch,
  localSort,
  onLocalSort,
  onMaterialSelect,
  filters,
}: SearchResultsProps) => {
  const headerSort = isLocalSearch && localSort ? [localSort] : sort;
  const { hierachyData } = useContext<SettingsContextValue>(SettingsContext);

  const { activeIndustry, activeApplicationClass, activeApplication } =
    useMemo(() => {
      const pick = (type: string) =>
        (
          filters?.find((f) => f.type === type)?.value as
            | Array<{ value: string }>
            | undefined
        )?.[0]?.value;
      return {
        activeIndustry: pick("industry"),
        activeApplicationClass: pick("applicationClass"),
        activeApplication: pick("application"),
      };
    }, [filters]);
  const onSortChangeForProperty = (property: string) => {
    if (sort?.every((s) => s.property !== property)) {
      onSortChange?.([{ property, direction: "asc" }]);
    }
    if (sort?.some((s) => s.property === property && s.direction === "asc")) {
      onSortChange?.([{ property, direction: "desc" }]);
    }
    if (sort?.some((s) => s.property === property && s.direction === "desc")) {
      onSortChange?.([]);
    }
  };
  const onLocalSortChangeForProperty = (property: string) => {
    const current = headerSort?.find((s) => s.property === property)?.direction;
    if (current === "desc" && localSort) {
      onLocalSort?.(undefined);
      return;
    }
    onLocalSort?.({ property, direction: current === "asc" ? "desc" : "asc" });
  };

  const { settings } = useContext<SettingsContextValue>(SettingsContext);
  const { user } = useAuthUser();
  const isInternal: boolean = checkInternalUser(user, settings);
  const { t } = useTranslation();

  const headers: Array<{
    align?: "left" | "right" | "center";
    sortProperty?: string;
    label: any;
  }> = [
    { label: <DeltaELabel />, align: "right", sortProperty: "deltaE" },
    {
      label: `${t("src_table_column_color")}`,
      align: "center",
      sortProperty: "color",
    },
    { label: `${t("src_table_column_grade")}`, sortProperty: "grade" },
    {
      label: `${t("src_table_column_color_code")}`,
      sortProperty: "finalColorCode",
    },
    { label: `${t("src_table_column_opacity")}`, align: "center" },
    { label: `${t("src_table_column_color_status")}`, align: "center" },
    { label: "" },
  ];

  return (
    <TableContainer>
      <Table sx={{ minWidth: "650px" }}>
        <TableHead>
          <TableRow>
            {headers.map(
              (header) =>
                !(!isInternal && header.label === "Color Status") && (
                  <TableCell
                    key={header.label}
                    sx={{
                      paddingLeft:
                        header.align === "center" && header.sortProperty
                          ? 3
                          : undefined,
                      paddingRight:
                        header.align === "center" && header.sortProperty
                          ? 0
                          : undefined,
                    }}
                    variant="head"
                    align={header.align}
                    sortDirection={
                      headerSort?.find(
                        (value) => value.property === header.sortProperty,
                      )?.direction ?? false
                    }
                  >
                    {header.sortProperty && (
                      <TableSortLabel
                        IconComponent={ArrowDropDownIcon}
                        onClick={() => {
                          isLocalSearch
                            ? onLocalSortChangeForProperty(header.sortProperty!)
                            : onSortChangeForProperty(header.sortProperty!);
                        }}
                        active={headerSort?.some(
                          (value) => value.property === header.sortProperty,
                        )}
                        direction={
                          headerSort?.find(
                            (value) => value.property === header.sortProperty,
                          )?.direction
                        }
                      >
                        {header.label}
                      </TableSortLabel>
                    )}
                    {!header.sortProperty && header.label}
                  </TableCell>
                ),
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading && values?.length === 0 && (
            <NoSearchResultsTableRow onFilterReset={onFilterReset} />
          )}
          {!loading &&
            values?.map((row: ColoredMaterial) => (
              <SearchResultsTableRow
                material={row}
                target={target}
                isInternal={isInternal}
                key={row.eljExperimentId + row.eljExperimentBatch}
                sx={{
                  "&:last-of-type > *": { borderBottom: "none !important" },
                }}
                hierachyData={hierachyData}
                onMaterialSelect={onMaterialSelect}
                activeIndustry={activeIndustry}
                activeApplicationClass={activeApplicationClass}
                activeApplication={activeApplication}
              />
            ))}
          {loading && <SearchResultsLoadingTableRow />}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
